import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { TDS_SECTIONS, findTdsSection } from '@fint/validation';
import { add, currencyMeta, format, formatHuman, formatRateHuman, impliedRate, parse, parseRate, subtract, toBase, zero } from '@fint/money';
import { api, ApiError } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useBaseCurrency, useToday } from '@/hooks/useOrgSettings';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { formatDate } from '@/lib/dates';
import { activeOnly } from '@/features/common/Drafts';
import { Button, Input, Money, Sheet, SheetBody, SheetFooter, SheetForm, SheetHeader } from '@/features/common/ui';
import { rateHint, useRateQuote, type RateSource } from '@/features/common/CurrencyRate';

interface OpenInvoice {
  id: string;
  number: string;
  date: string;
  dueDate: string | null;
  total: string;
  balanceDue: string;
  currency: string;
}

interface CustomerOption {
  id: string;
  displayName: string;
  currency?: string | null;
}

/**
 * Where the drawer opens from: the Payments page, or ONE invoice's page -
 * then the customer is known, the invoice is the allocation, and the amount
 * starts as its balance so a part-payment is the one figure the person
 * changes. The rest of the form is the same either way.
 */
export interface RecordPaymentPrefill {
  customerId: string;
  invoiceId: string;
  balanceDue: string;
  /** The invoice's currency, which is the payment's. */
  currency?: string;
}

export function RecordPaymentDrawer({
  onClose,
  prefill,
  onRecorded,
}: {
  onClose: () => void;
  prefill?: RecordPaymentPrefill;
  /** The new payment's id, for a page that wants to open its receipt strip. */
  onRecorded?: (payment: { id: string; number: string }) => void;
}) {
  useEscapeToClose(onClose);
  const org = useActiveOrg();
  const baseCurrency = useBaseCurrency();
  const today = useToday();
  const queryClient = useQueryClient();

  const [customerId, setCustomerId] = useState(prefill?.customerId ?? '');
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState(prefill?.balanceDue ?? '');
  const [mode, setMode] = useState('bank_transfer');
  const [reference, setReference] = useState('');
  /**
   * ── A PAYMENT IN ANOTHER CURRENCY ──
   * What the customer paid is in the invoice's currency; what the bank
   * credited is in the book's. Either the rupees or a rate is typed and
   * the other is derived on screen with the same arithmetic the server
   * uses - and the server re-derives the rate from the two amounts, so a
   * stale rate on this form cannot disagree with the cash.
   */
  const [baseAmount, setBaseAmount] = useState('');
  const [typedRate, setTypedRate] = useState('');
  const [remittance, setRemittance] = useState('');
  const [bankCharges, setBankCharges] = useState('');
  const [tdsSection, setTdsSection] = useState('');
  const [tdsRate, setTdsRate] = useState('');
  const [tdsAmount, setTdsAmount] = useState('');
  const [tan, setTan] = useState('');
  const [allocations, setAllocations] = useState<Record<string, string>>(prefill ? { [prefill.invoiceId]: prefill.balanceDue } : {});

  const customers = useQuery({
    queryKey: ['customers', org?.id, 'picker'],
    queryFn: () => api.get<{ items: CustomerOption[] }>('/customers?status=active&limit=200').then(activeOnly),
    enabled: !!org?.id,
  });

  const openInvoices = useQuery({
    queryKey: ['open-invoices', org?.id, customerId],
    queryFn: () => api.get<{ items: OpenInvoice[] }>(`/payments/open-invoices?customerId=${customerId}`),
    enabled: !!customerId,
  });

  /**
   * The payment's currency: the currency of the invoices it settles - all
   * of them must agree - else the customer's, else the book's. A customer
   * with a £ invoice and a ₹ invoice pays them as two payments.
   */
  const customer = customers.data?.items.find((c) => c.id === customerId);
  const ticked = (openInvoices.data?.items ?? []).filter((inv) => (allocations[inv.id] ?? '').trim() !== '');
  const currency = ticked[0]?.currency ?? prefill?.currency ?? customer?.currency ?? baseCurrency;
  const mixed = ticked.find((inv) => inv.currency !== currency);
  const foreign = currency !== baseCurrency;

  const money = (value: string) => {
    try {
      return parse(value || '0', currency);
    } catch {
      return zero(currency);
    }
  };
  const baseMoney = (value: string) => {
    try {
      return parse(value || '0', baseCurrency);
    } catch {
      return zero(baseCurrency);
    }
  };

  const received = money(amount);
  const withheld = add(money(tdsAmount), money(bankCharges));
  const allocated = Object.values(allocations).reduce((sum, v) => add(sum, money(v)), zero(currency));

  /**
   * What this payment can discharge, mirrored from the server.
   *
   * The server is the authority - it recomputes all of this and refuses a bad
   * allocation. This is here so the person allocating can see the figure move
   * as they type, rather than discovering after Save that ₹90,000 plus ₹10,000
   * withheld was not the ₹1,00,000 they meant.
   */
  const settleable = add(received, withheld);
  const remaining = subtract(settleable, allocated);

  // The third field: rupees typed gives the rate; a rate typed gives the rupees; neither means the published rate for the day.
  const quote = useRateQuote(date, currency, baseCurrency, foreign && !baseAmount && !typedRate);
  const typedBase = baseMoney(baseAmount);
  const rateFromBase = foreign && typedBase.minor > 0 && received.minor > 0 ? impliedRate(typedBase, received) : undefined;
  const rateFromTyped = parseRate(typedRate);
  const effectiveRate = rateFromBase ?? rateFromTyped ?? (quote.data?.rate ?? undefined);
  const rateSource: RateSource = rateFromBase ? 'bank' : rateFromTyped ? 'manual' : (quote.data?.source ?? 'manual');
  const derivedBase = foreign && !rateFromBase && effectiveRate && received.minor > 0 ? toBase(received, baseCurrency, effectiveRate) : null;
  const creditedBase = foreign ? (typedBase.minor > 0 ? typedBase : derivedBase) : received;

  const record = useMutation({
    mutationFn: () =>
      api.post<{ id: string; number: string }>('/payments', {
        customerId,
        date,
        amount,
        currency,
        // What the account was credited wins; a rate is sent only when no
        // rupee figure was typed. Nothing on a base-currency payment.
        baseAmount: foreign && typedBase.minor > 0 ? baseAmount : undefined,
        exchangeRate: foreign && typedBase.minor <= 0 && effectiveRate ? effectiveRate : undefined,
        exchangeRateSource: foreign && typedBase.minor <= 0 && effectiveRate ? rateSource : undefined,
        remittanceReference: foreign && remittance.trim() ? remittance.trim() : undefined,
        mode,
        referenceNumber: reference || undefined,
        bankCharges: money(bankCharges).minor > 0 ? bankCharges : undefined,
        tds:
          money(tdsAmount).minor > 0
            ? {
                section: tdsSection,
                rate: Number(tdsRate),
                amount: tdsAmount,
                deductorTan: tan || undefined,
              }
            : undefined,
        allocations: Object.entries(allocations)
          .filter(([, v]) => money(v).minor > 0)
          .map(([invoiceId, v]) => ({ invoiceId, amount: v })),
      }),
    onSuccess: (payment) => {
      void queryClient.invalidateQueries({ queryKey: ['payments', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoice-payments', org?.id] });
      // The invoice page's own record and its document view: the balance and the status moved.
      void queryClient.invalidateQueries({ queryKey: ['invoice', org?.id] });
      void queryClient.invalidateQueries({ queryKey: ['invoice-document', org?.id] });
      // An export's realisation is read off its payments (and the FIRA above).
      void queryClient.invalidateQueries({ queryKey: ['exports', org?.id] });
      onRecorded?.(payment);
      onClose();
    },
  });

  const error = record.error instanceof ApiError ? record.error : undefined;
  const tdsIncomplete = money(tdsAmount).minor > 0 && !tdsSection;
  const rateMissing = foreign && !effectiveRate;

  /** Fill every invoice up to what is left, oldest first - the common case. */
  const autoAllocate = () => {
    let left = settleable;
    const next: Record<string, string> = {};
    // Only invoices in the payment's currency: a pound cannot fill a rupee balance.
    for (const invoice of (openInvoices.data?.items ?? []).filter((inv) => inv.currency === currency)) {
      if (left.minor <= 0) break;
      const balance = parse(invoice.balanceDue, invoice.currency);
      const take = left.minor < balance.minor ? left : balance;
      next[invoice.id] = format(take);
      left = subtract(left, take);
    }
    setAllocations(next);
  };

  const suggestedTds = useMemo(() => {
    const section = findTdsSection(tdsSection);
    return section ? section.rate : undefined;
  }, [tdsSection]);

  return (
    <Sheet label="Record payment" onClose={onClose} wide>
      <SheetHeader title="Record payment" onClose={onClose} />
      <SheetForm
        onSubmit={(e) => {
          e.preventDefault();
          record.mutate();
        }}
      >
        <SheetBody>
          <label className="block text-sm">
            <span className="mb-1.5 block font-medium">Customer</span>
            <select
              required
              className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-3"
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                setAllocations({});
              }}
            >
              <option value="">Choose a customer…</option>
              {customers.data?.items.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </label>

          <div className="grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
            <Input label="Date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
            <Input
              label={foreign ? `Received (${currency})` : 'Amount received'}
              required
              inputMode="decimal"
              placeholder="90000.00"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                // From an invoice page the payment IS this invoice's: the
                // allocation follows the amount, capped by what is owed.
                if (prefill) {
                  const typed = money(e.target.value);
                  const owed = money(prefill.balanceDue);
                  setAllocations({ [prefill.invoiceId]: format(typed.minor < owed.minor ? typed : owed) });
                }
              }}
              error={error?.fieldError('amount')}
            />
          </div>

          {foreign ? (
            <div className="rounded-md border border-border p-3" data-testid="payment-fx">
              <p className="text-sm font-medium">
                Paid in {currencyMeta(currency).name}; your account is in {baseCurrency}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Type what the bank credited, or the rate - the other follows. The invoice settles in {currency}; the books take the {baseCurrency}.
              </p>
              <div className="mt-3 grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
                <Input
                  label={`Credited to your account (${baseCurrency})`}
                  inputMode="decimal"
                  placeholder="125574.80"
                  value={baseAmount}
                  onChange={(e) => setBaseAmount(e.target.value)}
                  hint={derivedBase && !baseAmount ? `≈ ${formatHuman(derivedBase)} at the rate` : 'The figure on the bank statement.'}
                  error={error?.fieldError('baseAmount')}
                  data-testid="payment-base-amount"
                />
                <Input
                  label={`Rate: 1 ${currency} = ${currencyMeta(baseCurrency).symbol}`}
                  inputMode="decimal"
                  placeholder="127.10"
                  value={rateFromBase ? formatRateHuman(rateFromBase) : typedRate || (quote.data?.rate && !typedRate ? formatRateHuman(quote.data.rate) : '')}
                  onChange={(e) => setTypedRate(e.target.value)}
                  disabled={!!rateFromBase}
                  hint={rateHint(currency, baseCurrency, rateSource, quote.data, quote.isLoading, !!typedRate)}
                  error={error?.fieldError('exchangeRate')}
                  data-testid="payment-exchange-rate"
                />
              </div>
              {rateFromBase === undefined && rateFromTyped && received.minor > 0 ? (
                <p className="mt-1 text-xs text-muted-foreground" data-testid="payment-derived-base">
                  {formatHuman(received)} at {formatRateHuman(rateFromTyped)} &rarr;{' '}
                  <Money money={toBase(received, baseCurrency, rateFromTyped)} value={0} currency={baseCurrency} /> credited.
                </p>
              ) : null}
              <div className="mt-3">
                <Input
                  label="FIRA / FIRC number"
                  value={remittance}
                  onChange={(e) => setRemittance(e.target.value)}
                  hint="The bank's inward remittance advice for this payment – your proof the export was paid."
                />
              </div>
            </div>
          ) : null}

          <div className="grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="mb-1.5 block font-medium">Mode</span>
              <select
                className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-3"
                value={mode}
                onChange={(e) => setMode(e.target.value)}
              >
                {['bank_transfer', 'cash', 'cheque', 'card', 'upi', 'online', 'other'].map((m) => (
                  <option key={m} value={m}>
                    {m.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </label>
            <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} />
          </div>

          {/*
            Withholding. Its own block with its own explanation, because the
            arithmetic is genuinely surprising the first time: the invoice
            settles for MORE than the money that arrived, and that is correct.
          */}
          <div className="rounded-md border border-border p-3">
            <p className="text-sm font-medium">Deducted before you were paid</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              TDS your customer withheld, and anything the bank took in transit. Both settle the
              invoice — the money is not lost, it went somewhere else.
            </p>

            <div className="mt-3 grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
              <label className="block text-sm">
                <span className="mb-1.5 block font-medium">TDS section</span>
                <select
                  className="min-h-11 w-full min-w-0 rounded-md border border-input bg-background text-base sm:min-h-9 sm:text-sm px-3"
                  value={tdsSection}
                  onChange={(e) => {
                    setTdsSection(e.target.value);
                    const section = findTdsSection(e.target.value);
                    if (section) setTdsRate(String(section.rate));
                  }}
                >
                  <option value="">None</option>
                  {TDS_SECTIONS.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code} — {s.label}
                    </option>
                  ))}
                </select>
              </label>
              <Input
                label="Rate %"
                inputMode="decimal"
                value={tdsRate}
                onChange={(e) => setTdsRate(e.target.value)}
                hint={suggestedTds !== undefined ? `Usually ${suggestedTds}%` : undefined}
              />
            </div>

            <div className="mt-3 grid gap-3 [&>*]:min-w-0 sm:grid-cols-2">
              <Input
                label="TDS deducted"
                inputMode="decimal"
                placeholder="10000.00"
                value={tdsAmount}
                onChange={(e) => setTdsAmount(e.target.value)}
                hint="What they actually deducted, not what the rate implies."
                error={tdsIncomplete ? 'Pick the TDS section too.' : undefined}
              />
              <Input
                label="Their TAN"
                value={tan}
                onChange={(e) => setTan(e.target.value.toUpperCase())}
                hint="On their Form 16A. Needed to chase a 26AS mismatch."
              />
            </div>

            <div className="mt-3">
              <Input
                label="Bank charges"
                inputMode="decimal"
                value={bankCharges}
                onChange={(e) => setBankCharges(e.target.value)}
              />
            </div>
          </div>

          {customerId ? (
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-sm font-medium">Apply to invoices</p>
                <Button type="button" variant="outline" size="sm" onClick={autoAllocate}>
                  Oldest first
                </Button>
              </div>

              {!openInvoices.data?.items.length ? (
                <p className="text-sm text-muted-foreground">
                  Nothing outstanding for this customer. The whole amount becomes credit on their
                  account.
                </p>
              ) : (
                <div className="space-y-2">
                  {mixed ? (
                    <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive" data-testid="payment-mixed-currency">
                      {ticked[0]?.number} is in {currency} and {mixed.number} in {mixed.currency}. Record them as two payments.
                    </p>
                  ) : null}
                  {openInvoices.data.items.map((invoice) => (
                    <div key={invoice.id} className="flex items-center gap-3 text-sm">
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">{invoice.number}</span>
                        <span className="ml-2 text-muted-foreground">
                          {formatDate(invoice.date)} · <Money value={invoice.balanceDue} currency={invoice.currency} /> due
                        </span>
                      </div>
                      <input
                        aria-label={`Apply to ${invoice.number}`}
                        inputMode="decimal"
                        className="min-h-11 w-32 min-w-0 rounded-md border border-input bg-background sm:min-h-8 px-2 text-right text-sm"
                        value={allocations[invoice.id] ?? ''}
                        onChange={(e) =>
                          setAllocations((prev) => ({ ...prev, [invoice.id]: e.target.value }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}

          <dl className="space-y-1 rounded-md bg-muted/40 p-3 text-sm">
            <Line label="Received" value={format(received)} currency={currency} />
            {foreign && creditedBase ? <Line label={`Credited in ${baseCurrency}`} value={format(creditedBase)} currency={baseCurrency} /> : null}
            {withheld.minor > 0 ? (
              <Line label="Withheld before payment" value={format(withheld)} currency={currency} />
            ) : null}
            <Line label="Can settle" value={format(settleable)} currency={currency} strong />
            <Line label="Applied" value={format(allocated)} currency={currency} />
            <Line
              label={remaining.minor < 0 ? 'Over-applied' : 'Left as credit'}
              value={format(remaining)}
              currency={currency}
              tone={remaining.minor < 0 ? 'bad' : undefined}
            />
          </dl>

          {error ? (
            <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error.message}
            </p>
          ) : null}
        </SheetBody>
        <SheetFooter className="justify-end">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={record.isPending} disabled={tdsIncomplete || !!mixed || rateMissing}>
            Record payment
          </Button>
        </SheetFooter>
      </SheetForm>
    </Sheet>
  );
}

function Line({
  label,
  value,
  currency,
  strong,
  tone,
}: {
  label: string;
  value: string;
  currency: string;
  strong?: boolean;
  tone?: 'bad';
}) {
  return (
    <div className={`flex items-center justify-between ${strong ? 'font-semibold' : ''}`}>
      <dt className={tone === 'bad' ? 'text-destructive' : 'text-muted-foreground'}>{label}</dt>
      <dd className={tone === 'bad' ? 'text-destructive' : ''}>
        <Money value={value} currency={currency} />
      </dd>
    </div>
  );
}
